"use client";

import { ArrowRight, FileText, Hourglass, ChartPie as PieChart, ShieldCheck } from 'lucide-react';
import { Card } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function HowWeSupportYou() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const grid = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const services = [{
    eyebrow: "CORE SERVICE",
    title: "Respite Care Support",
    description: "We provide families with reliable, compassionate respite care, ensuring caregivers have the time they need to recharge while their loved ones are in a safe, engaging environment.",
    icon: Hourglass,
    featured: false
  }, {
    eyebrow: "FEATURED",
    title: "Inclusive Programs",
    description: "Our community-focused programs are designed to celebrate neurodiversity, offering inclusive activities where every child and family member can thrive, connect, and belong.",
    icon: PieChart,
    featured: true
  }, {
    eyebrow: "CORE SERVICE",
    title: "Advocacy & Resources",
    description: "Navigating systems can be complex. We offer bilingual resources and dedicated advocacy to help families secure the support and services they deserve with confidence.",
    icon: ShieldCheck,
    featured: false
  }, {
    eyebrow: "CORE SERVICE",
    title: "Family Partnerships",
    description: "Building a stronger neighborhood together. We partner with local organizations to host family-centered events that foster connection, understanding, and lasting community bonds.",
    icon: FileText,
    featured: false
  }];
  return <section data-section-id="800" id="how-we-support-you" className="bg-background text-foreground py-20 md:py-32">
      <div className="mx-auto w-full max-w-7xl px-6 lg:px-10">
        {}
        <div ref={header.ref} className={`grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-8 transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="h-px w-8 bg-secondary" aria-hidden="true" />
              <span className="font-default text-xs uppercase tracking-[0.22em] text-secondary">
                Our Mission
              </span>
            </div>
            <h2 className="font-default font-semibold text-foreground mt-6 leading-[1.08] tracking-tight" style={{
            fontSize: "clamp(2.25rem, 3.5vw, 3.5rem)"
          }}>
              Empowering families through community and care.
            </h2>
          </div>

          <div className="lg:col-span-6 lg:col-start-7 lg:pt-4">
            <p className="text-[17px] leading-[1.7] text-foreground/80 font-default">
              At Hope Torchlighters, we believe every family deserves a village. We provide the resources, advocacy, and respite care necessary to ensure neurodiverse families feel supported, seen, and celebrated in every season of life.
            </p>
            <Link to="/ProgramsPage" className="group mt-8 inline-flex items-center gap-2 font-default text-sm uppercase tracking-[0.18em] text-secondary hover:text-secondary/80 transition-colors">
              <span className="relative">
                Explore our programs
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-secondary transition-all duration-300 group-hover:w-full" />
              </span>
              <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        {}
        <div ref={grid.ref} className={`mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-6 transition-all duration-700 ease-out delay-100 ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {services.map((service, i) => {
          const Icon = service.icon;
          const isFeatured = service.featured;
          return <Card data-index={i} key={service.title} className={`group relative overflow-hidden rounded-lg border p-10 transition-all duration-500 ease-out hover:-translate-y-1 ${isFeatured ? "bg-primary text-white border-primary hover:shadow-xl" : "bg-card border-border hover:shadow-lg hover:border-secondary/40"}`}>
                {}
                <div className={`h-px transition-all duration-500 ease-out ${isFeatured ? "bg-secondary" : "bg-secondary"} w-6 group-hover:w-12`} aria-hidden="true" />

                {}
                <div className={`mt-4 font-default text-[11px] uppercase tracking-[0.22em] ${isFeatured ? "text-white/60" : "text-foreground/50"}`}>
                  {service.eyebrow}
                </div>

                {}
                <div className="mt-10">
                  <Icon strokeWidth={1.25} className={`size-10 transition-colors duration-500 ${isFeatured ? "text-white group-hover:text-secondary" : "text-primary group-hover:text-secondary"}`} />
                </div>

                {}
                <h3 className={`font-default font-semibold mt-8 leading-tight tracking-tight ${isFeatured ? "text-white" : "text-foreground"}`} style={{
              fontSize: "26px"
            }}>
                  {service.title}
                </h3>

                {}
                <p className={`mt-5 text-[15px] leading-[1.65] font-default ${isFeatured ? "text-white/75" : "text-foreground/70"}`}>
                  {service.description}
                </p>

                {}
                <div className="mt-10">
                  <Link to="/ConnectPage" className="inline-flex items-center gap-2 font-default text-xs uppercase tracking-[0.2em] text-secondary hover:text-secondary/80">
                    <span className="relative">
                      Learn more
                      <span className="absolute -bottom-1 left-0 h-px w-0 bg-secondary transition-all duration-300 group-hover:w-full" />
                    </span>
                    <ArrowRight className="size-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}